import { create } from "zustand";
import { persist } from "zustand/middleware";

import { widthFor, useSidebarStore } from "./sidebarStore";

const DEFAULT_WIDTH = 540;
const MIN_WIDTH = 240;

interface ChatPanelState {
  isCollapsed: boolean;
  width: number;
  /** Full-screen chat (world hidden). Mobile defaults to true (binary view). */
  boardHidden: boolean;
  /** Mutes every app sound effect (agent-done, agent-start, poke). Persisted. */
  soundsMuted: boolean;
  setWidth: (w: number) => void;
  setCollapsed: (v: boolean) => void;
  toggleCollapsed: () => void;
  setBoardHidden: (v: boolean) => void;
  toggleBoard: () => void;
  toggleSoundsMuted: () => void;
}

function clampWidth(w: number): number {
  const sidebar =
    window.innerWidth >= 1024 ? widthFor(useSidebarStore.getState().mode) : widthFor("rail");
  const available = window.innerWidth - sidebar;
  const maxPx = available - 16; // 16px breathing room for resize handle
  return Math.min(Math.max(w, MIN_WIDTH), maxPx);
}

export const useChatPanelStore = create<ChatPanelState>()(
  persist(
    (set) => ({
      isCollapsed: false,
      width: DEFAULT_WIDTH,
      boardHidden: typeof window !== "undefined" ? window.innerWidth < 1024 : true,
      soundsMuted: false,
      setWidth: (w: number) => set({ width: clampWidth(w) }),
      setCollapsed: (v: boolean) => set({ isCollapsed: v }),
      toggleCollapsed: () => set((s) => ({ isCollapsed: !s.isCollapsed })),
      setBoardHidden: (v: boolean) => set({ boardHidden: v }),
      toggleBoard: () => set((s) => ({ boardHidden: !s.boardHidden })),
      toggleSoundsMuted: () => set((s) => ({ soundsMuted: !s.soundsMuted })),
    }),
    {
      name: "clawcraft-chat-panel",
      version: 1,
      // Only the mute preference persists; width/boardHidden stay per-session
      // (boardHidden's default is viewport-derived and must not be revived).
      partialize: (s) => ({ soundsMuted: s.soundsMuted }),
    },
  ),
);

export { MIN_WIDTH };
